import { base64ToUtf8, utf8ToBase64 } from '../utils/encodeOrDecode';

class SecureLocalStorage {
  key: string;
  channel?: BroadcastChannel; // Optional channel for WalletDetails only

  constructor(key: string) {
    this.key = key;
    // Initialize BroadcastChannel only for WalletDetails
    if (key === 'token') {
      this.channel = new BroadcastChannel('token');
    }
    if (key === 'WalletDetails') {
      this.channel = new BroadcastChannel('walletDetailsChannel');
    }
  }

  /**
   * Store the value in localStorage after encoding it as a Base64 string.
   * Broadcast the value if the key is WalletDetails.
   *
   * @param value The value to store (can be any data type).
   */
  setValue = (value: unknown): void => {
    const encodedValue = utf8ToBase64(JSON.stringify(value));

    localStorage.setItem(this.key, encodedValue);
    // Broadcast only for WalletDetails
    if ((this.key === 'WalletDetails' || this.key === 'token') && this.channel) {
      this.channel.postMessage(value); // Send raw value
    }
  };

  /**
   * Retrieve the value from localStorage, decode it from Base64 to UTF-8,
   * then parse it back into the original value.
   *
   * @returns The decoded value or undefined if not found.
   */
  getValue = (): unknown => {
    if (typeof window !== 'undefined') {
      const response = localStorage.getItem(this.key);

      if (response) {
        const decodedString = base64ToUtf8(response);

        try {
          return JSON.parse(decodedString);
        } catch (error) {
          console.debug('Error parsing JSON from localStorage:', error);

          return null;
        }
      }
    }

    return undefined;
  };

  /**
   * Clear the value from localStorage.
   * Broadcast a null value if the key is WalletDetails.
   */
  clear = (): void => {
    localStorage.removeItem(this.key);
    // Broadcast only for WalletDetails
    if ((this.key === 'WalletDetails' || this.key === 'token') && this.channel) {
      this.channel.postMessage(null); // Indicate cleared state
    }
  };

  /**
   * Close the BroadcastChannel (for WalletDetails only).
   * Called when the instance is no longer needed (optional, for explicit cleanup).
   */
  closeChannel = (): void => {
    if (this.channel) {
      this.channel.close();
      this.channel = undefined;
    }
  };
}

export const StorageService = {
  token: new SecureLocalStorage('token'),
  walletDetails: new SecureLocalStorage('WalletDetails'),
  tokenType: new SecureLocalStorage('tokenType'),
  metaData: new SecureLocalStorage('MetaData'),
  accessToken: new SecureLocalStorage('act_0'),
  networkDetails: new SecureLocalStorage('NetworkDetails'),
  requiredGas: new SecureLocalStorage('r_gas'),
  loginTime: new SecureLocalStorage('wl_t'),
  transactionHash: new SecureLocalStorage('TransactionHash')
};
